// Webhook configuration
const WEBHOOK_CONFIG = {
  url: 'https://hook.integrator.boost.space/990kyr6ocu8434njc771x8jakpxq2k5t',
  timeout: 10000 // 10 second timeout
};

// Helper function to format arrays
const formatArray = (arr) => Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : 'Not specified';

// Main function to send quote request via webhook
export const sendQuoteRequest = async (formData) => {
  try {
    console.log('🚀 Sending quote request to webhook...');

    // Prepare the data payload
    const payload = {
      // Timestamp
      timestamp: new Date().toISOString(),
      submission_date: new Date().toLocaleDateString('en-GB'),
      submission_time: new Date().toLocaleTimeString('en-GB'),

      // Customer Information
      customer: {
        fullName: formData.fullName || 'Not provided',
        email: formData.email || 'Not provided',
        phone: formData.phone || 'Not provided',
        postcode: formData.postcode || 'Not provided',
        message: formData.message || 'No additional message provided',
        consent: formData.consent || false
      },

      // Project Details
      project: {
        products: formData.products || [],
        propertyType: formData.propertyType || 'Not specified',
        timeline: formData.timeline || 'Not specified',
        budget: formData.budget || 'Not specified'
      },

      // Windows Information
      windows: {
        types: formData.windowTypes || [],
        count: formData.windowCount || 'Not specified'
      },

      // Doors Information
      doors: {
        types: formData.doorTypes || []
      },

      // Conservatory Information
      conservatory: {
        need: formData.conservatoryNeed || 'Not specified',
        style: formData.conservatoryStyle || 'Not specified'
      },

      // Preferences
      preferences: {
        materials: formData.materials || [],
        glazingType: formData.glazingType || [],
        energyEfficiency: formData.energyEfficiency || 'Not specified',
        color: formData.color || 'Not specified'
      },

      // Formatted message for easy reading
      formattedMessage: `
🏠 NEW DOUBLE GLAZING QUOTE REQUEST
================================

👤 CUSTOMER INFORMATION:
• Name: ${formData.fullName || 'Not provided'}
• Email: ${formData.email || 'Not provided'}
• Phone: ${formData.phone || 'Not provided'}
• Postcode: ${formData.postcode || 'Not provided'}
• Consent Given: ${formData.consent ? 'YES' : 'NO'}

💬 CUSTOMER MESSAGE:
${formData.message ? `"${formData.message}"` : 'No additional message provided'}

📋 PROJECT DETAILS:
• Products Required: ${formatArray(formData.products)}
• Property Type: ${formData.propertyType || 'Not specified'}
• Timeline: ${formData.timeline || 'Not specified'}
• Budget: ${formData.budget || 'Not specified'}

🪟 WINDOWS:
• Types: ${formatArray(formData.windowTypes)}
• Quantity: ${formData.windowCount || 'Not specified'}

🚪 DOORS:
• Types: ${formatArray(formData.doorTypes)}

🏡 CONSERVATORY:
• Requirements: ${formData.conservatoryNeed || 'Not specified'}
• Style Preference: ${formData.conservatoryStyle || 'Not specified'}

⚙️ PREFERENCES:
• Materials: ${formatArray(formData.materials)}
• Glazing Type: ${formatArray(formData.glazingType)}
• Energy Efficiency: ${formData.energyEfficiency || 'Not specified'}
• Color/Finish: ${formData.color || 'Not specified'}

📅 SUBMITTED: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}

⚡ PRIORITY: Please contact this customer within 24 hours for their free quote.
      `.trim(),

      // Source information
      source: 'double-glazing-quote-form',
      version: '1.1'
    };

    console.log('📤 Sending payload to webhook:', WEBHOOK_CONFIG.url);
    console.log('📋 Payload preview:', {
      customer: payload.customer.fullName,
      products: payload.project.products,
      message: payload.customer.message,
      consent: payload.customer.consent,
      timestamp: payload.timestamp
    });

    // Send POST request to webhook
    const response = await fetch(WEBHOOK_CONFIG.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(WEBHOOK_CONFIG.timeout)
    });

    // Check if the response is ok
    if (!response.ok) {
      throw new Error(`Webhook responded with status: ${response.status} ${response.statusText}`);
    }

    // Try to parse response (some webhooks return JSON, others don't)
    let responseData;
    try {
      const responseText = await response.text();
      responseData = responseText ? JSON.parse(responseText) : { status: 'success' };
    } catch (parseError) {
      // If parsing fails, assume success if status was ok
      responseData = { status: 'success', raw: 'non-json-response' };
    }

    console.log('✅ Webhook Success:', {
      status: response.status,
      statusText: response.statusText,
      data: responseData
    });

    return {
      success: true,
      message: 'Quote request sent successfully to webhook!',
      method: 'webhook',
      response: responseData,
      status: response.status
    };

  } catch (error) {
    console.error('❌ Webhook Error:', error);

    // Provide specific error messages based on error type
    let errorMessage = 'Failed to send quote request';
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      errorMessage = 'Request timed out - please try again';
    } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      errorMessage = 'Network error - please check your connection';
    } else if (error.message.includes('status:')) {
      errorMessage = `Server error: ${error.message}`;
    } else {
      errorMessage = error.message || 'Unknown error occurred';
    }

    // For webhook failures, we can still offer the fallback email option
    console.log('💡 Webhook failed, fallback email available');
    throw new Error(errorMessage);
  }
};

// Fallback email function (opens email client with pre-filled content)
export const sendFallbackEmail = (formData) => {
  const subject = `🏠 New Double Glazing Quote Request - ${formData.fullName} (${formData.postcode})`;
  
  const body = `
🏠 NEW DOUBLE GLAZING QUOTE REQUEST
================================

👤 CUSTOMER INFORMATION:
• Name: ${formData.fullName || 'Not provided'}
• Email: ${formData.email || 'Not provided'}
• Phone: ${formData.phone || 'Not provided'}
• Postcode: ${formData.postcode || 'Not provided'}
• Consent Given: ${formData.consent ? 'YES' : 'NO'}

💬 CUSTOMER MESSAGE:
${formData.message ? `"${formData.message}"` : 'No additional message provided'}

📋 PROJECT DETAILS:
• Products Required: ${formatArray(formData.products)}
• Property Type: ${formData.propertyType || 'Not specified'}
• Timeline: ${formData.timeline || 'Not specified'}
• Budget: ${formData.budget || 'Not specified'}

🪟 WINDOWS:
• Types: ${formatArray(formData.windowTypes)}
• Quantity: ${formData.windowCount || 'Not specified'}

🚪 DOORS:
• Types: ${formatArray(formData.doorTypes)}

🏡 CONSERVATORY:
• Requirements: ${formData.conservatoryNeed || 'Not specified'}
• Style Preference: ${formData.conservatoryStyle || 'Not specified'}

⚙️ PREFERENCES:
• Materials: ${formatArray(formData.materials)}
• Glazing Type: ${formatArray(formData.glazingType)}
• Energy Efficiency: ${formData.energyEfficiency || 'Not specified'}
• Color/Finish: ${formData.color || 'Not specified'}

📅 SUBMITTED: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}

⚡ PRIORITY: Please contact this customer within 24 hours for their free quote.
  `.trim();

  const mailtoLink = `mailto:doubleglazingquoted@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  
  console.log('📧 Opening email client with pre-filled quote request...');
  window.open(mailtoLink, '_blank');
  
  return true;
};

// Test function to verify webhook connectivity
export const testWebhook = async () => {
  try {
    const testPayload = {
      test: true,
      timestamp: new Date().toISOString(),
      message: 'This is a test request to verify webhook connectivity',
      customer: {
        fullName: 'Test User',
        email: 'test@example.com',
        phone: '01234567890',
        postcode: 'SW1A 1AA',
        message: 'This is a test message',
        consent: true
      },
      project: {
        products: ['Windows'],
        propertyType: 'House',
        timeline: 'ASAP'
      }
    };

    console.log('🧪 Testing webhook connectivity...');

    const response = await fetch(WEBHOOK_CONFIG.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(testPayload),
      signal: AbortSignal.timeout(5000) // 5 second timeout for test
    });

    const responseData = response.ok ? await response.text() : null;

    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText,
      response: responseData,
      url: WEBHOOK_CONFIG.url
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      url: WEBHOOK_CONFIG.url
    };
  }
};

// Helper function to get webhook status
export const getWebhookStatus = () => {
  return {
    configured: true, // Always true since we have a hardcoded URL
    url: WEBHOOK_CONFIG.url,
    timeout: WEBHOOK_CONFIG.timeout,
    method: 'POST'
  };
};

// Legacy function name for compatibility (now points to webhook status)
export const getEmailJSStatus = () => {
  return {
    configured: true, // Always show as configured since webhook is set up
    method: 'webhook',
    url: WEBHOOK_CONFIG.url
  };
};